import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useUser } from '@/context/UserContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, BookOpen, MessageCircle, FileText, User, Menu, X, Copy, Check, Users } from 'lucide-react';
import MemeademicAdvisor from '@/components/MemeademicAdvisor';
import EnrollModal from '@/components/EnrollModal';
import { toast } from 'sonner';

// Contract Address Component for Navbar
const NavContractAddress = () => {
  const [copied, setCopied] = useState(false);
  const contractAddress = "E3CaJ12mcajpXKPvyjv61T2ivPXfW9KKiyriXXj9pmpU";
  const isComingSoon = true;

  const handleCopy = async () => {
    if (isComingSoon) return;
    try {
      await navigator.clipboard.writeText(contractAddress);
      setCopied(true);
      toast.success('CA copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      onClick={handleCopy}
      disabled={isComingSoon}
      data-testid="nav-contract-address"
      className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all ${
        isComingSoon 
          ? 'bg-degen-purple/10 border border-degen-purple/30 cursor-default' 
          : 'bg-pump-surface border border-white/10 hover:border-pump-green/50 cursor-pointer'
      }`}
    >
      <span className="text-zinc-500 font-medium">CA:</span>
      <span className={`font-mono ${isComingSoon ? 'text-degen-purple' : 'text-pump-green'}`}>
        {isComingSoon ? 'Coming Soon' : `${contractAddress.slice(0, 4)}...${contractAddress.slice(-4)}`}
      </span>
      {!isComingSoon && (
        copied ? <Check size={12} className="text-pump-green" /> : <Copy size={12} className="text-zinc-500" />
      )}
    </button>
  );
};

// X (Twitter) Button
const XSocialButton = () => (
  <a
    href="https://x.com/i/communities/1994609732997316747"
    target="_blank"
    rel="noopener noreferrer"
    data-testid="nav-x-social"
    className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-white/30 transition-all"
  >
    <svg viewBox="0 0 24 24" className="w-4 h-4 text-zinc-400 hover:text-white transition-colors" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  </a>
);

const Layout = () => {
  const { user } = useUser();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);

  const isGraduated = user?.progress && ['memeglish', 'memethematics', 'memestory', 'memeart'].every(
    c => user.progress[c]?.quiz_passed
  );

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/classes', label: 'Classes', icon: BookOpen },
    { path: '/notes', label: 'My Notes', icon: FileText },
    { path: '/profile', label: 'Profile', icon: User },
    ...(isGraduated ? [{ path: '/wall-of-traders', label: 'Wall of Traders', icon: Users }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-pump-black relative noise-bg pencil-cursor">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
              <div className="w-10 h-10 rounded-xl overflow-hidden shadow-neon">
                <img 
                  src="https://customer-assets.emergentagent.com/job_pumpology-edu/artifacts/xgvcffr1_IMG_0989.PNG" 
                  alt="Pump U Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-unbounded font-bold text-white text-lg hidden sm:block group-hover:text-pump-green transition-colors">
                PUMP<span className="text-pump-green">U</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-pump-green/20 text-pump-green'
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <item.icon size={18} />
                  <span className="font-jakarta font-medium text-sm">{item.label}</span>
                </Link>
              ))}
            </div>

            {/* User/Enroll Button */}
            <div className="flex items-center gap-3">
              {/* CA Address */}
              <NavContractAddress />
              
              {/* X Social Button */}
              <XSocialButton />
              
              {user ? (
                <Link
                  to="/profile"
                  data-testid="user-profile-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-pump-card border border-white/10 hover:border-pump-green/50 transition-all"
                >
                  <div className="w-6 h-6 rounded-full bg-pump-green/20 flex items-center justify-center">
                    <span className="text-pump-green text-xs font-bold">{user.username[0].toUpperCase()}</span>
                  </div>
                  <span className="text-white text-sm font-medium hidden sm:block">{user.username}</span>
                </Link>
              ) : (
                <button
                  onClick={() => setShowEnrollModal(true)}
                  data-testid="enroll-btn"
                  className="px-5 py-2 bg-pump-green text-black font-bold text-sm rounded-full hover:shadow-neon-strong transition-all duration-200 uppercase tracking-wider"
                >
                  Enroll Now
                </button>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-white"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-white/10 bg-pump-surface"
            >
              <div className="px-4 py-4 space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive(item.path)
                        ? 'bg-pump-green/20 text-pump-green'
                        : 'text-zinc-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <item.icon size={20} />
                    <span className="font-jakarta font-medium">{item.label}</span>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Memeademic Advisor FAB */}
      <MemeademicAdvisor />

      {/* Enroll Modal */}
      <EnrollModal open={showEnrollModal} onClose={() => setShowEnrollModal(false)} />
    </div>
  );
};

export default Layout;
